import { useCallback, useEffect, useMemo, useRef } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import { sum } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { Doughnut } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getMessage } from '@gef-ui/localization/messages';
import useThemeColor from '@gef-ui/material/hooks/useThemeColor';

import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import { fetchSelectedCampaignContacts } from 'features/views/dashboard/campaignManagement/actions';
import {
	getSelectedCampaign,
	getSelectedCampaignContacts,
} from 'features/views/dashboard/campaignManagement/selectors';
import { getRoutePath } from 'utils/routeHelper';

ChartJS.register(ArcElement);

export const ContactsGraph = () => {
	const dispatch = useDispatch();
	const chartRef = useRef(null);
	const navigate = useNavigate();

	const warning = useThemeColor('warning');
	const error = useThemeColor('error');
	const backgroundColor = useMemo(() => [error, warning], [error, warning]);

	const selectedCampaign = useSelector(getSelectedCampaign);
	const campaignContactsSummary = useSelector(getSelectedCampaignContacts);

	useEffect(() => {
		dispatch(fetchSelectedCampaignContacts(selectedCampaign));
	}, [dispatch, selectedCampaign]);

	const { mode: theme } = useColorScheme();

	const generateData = useMemo(() => {
		const orderedData = [campaignContactsSummary?.toAssign, campaignContactsSummary?.toRelease];

		if (campaignContactsSummary?.total > 0) {
			// used when we receive some actual data, not zeros

			return {
				datasets: [
					{
						data: orderedData,
						backgroundColor,
						borderRadius: 10,
						borderWidth: 3,
						borderColor: '#F5F5F5',
					},
				],
			};
		} else {
			return {
				// used when we receive zeros
				// just placeholder data, to create "disabled" feel > graph with one full value and ligthGrey color
				datasets: [
					{
						data: [100],
						backgroundColor: ['lightGrey'],
						borderRadius: 10,
						borderWidth: 3,
						borderColor: '#F5F5F5',
					},
				],
			};
		}
	}, [backgroundColor, campaignContactsSummary]);

	const options = {
		cutout: '85%',
		maintainAspectRatio: false,
		hover: { mode: null },
		plugins: {
			tooltip: {
				enabled: false,
			},
		},
	};

	const generatePlugins = useMemo(
		() => [
			{
				id: 'campaignManagement',
				beforeDraw(chart) {
					const data = chart?.data?.datasets[0]?.data;
					const { width } = chart;
					const { height } = chart;
					const { ctx } = chart;
					ctx.restore();

					ctx.font = `${(height / 4).toFixed(2)}px sans-serif`;
					const textCount = data.length === 2 ? sum(data) : 0;
					ctx.textAlign = 'center';
					ctx.fillStyle = theme === 'light' ? 'black' : 'white';
					ctx.fillText(textCount, width / 2, height / 2 - 5);
					ctx.restore();

					ctx.font = `${(height / 10).toFixed(2)}px sans-serif`;
					ctx.textAlign = 'center';
					ctx.fillText(getMessage('dashboard:campaignManagement.contacts'), width / 2, height / 2 + 25);

					ctx.save();
				},
			},
		],
		[theme],
	);

	const badgeSx = {
		'& .MuiBadge-dot': {
			marginTop: 1.25,
			marginRight: 2,
		},
	};

	const handleOpenCampaignDetail = useCallback(
		(selectedCampaignId) => () => {
			const { pathname, state } = getRoutePath('campaignManagement', { campaignId: selectedCampaignId }, true);
			navigate(
				{
					pathname,
					search: `?contactState=1`,
				},
				{ state },
			);
		},
		[navigate],
	);

	return (
		<Paper
			sx={{
				p: 3,
				backgroundColor: (theme) => theme.palette.background[100],
				borderRadius: 1,
				height: '100%',
			}}
		>
			<ComponentLoader fetchStorePath={fetch.constants.urls.campaignManagementContacts} forceInlineError>
				<Stack spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
					<Box sx={{ height: '160px', width: '160px', mx: 3 }}>
						<Doughnut
							ref={chartRef}
							data={generateData}
							options={options}
							redraw
							plugins={generatePlugins}
						/>
					</Box>
					<Stack
						spacing={1}
						sx={{
							justifyContent: 'center',
							width: '180px',
						}}
					>
						<Stack
							direction="row"
							sx={{
								justifyContent: 'space-between',
							}}
						>
							<Stack direction="row">
								<Badge sx={{ ...badgeSx }} variant="dot" color="error" />
								<Typography variant="body1">
									{getMessage('dashboard:campaignManagement.toBeAssigned')}
								</Typography>
							</Stack>
							<Typography
								variant="body1"
								sx={{
									fontWeight: 'bold',
								}}
							>
								{campaignContactsSummary?.toAssign}
							</Typography>
						</Stack>
						<Stack
							direction="row"
							sx={{
								justifyContent: 'space-between',
							}}
						>
							<Stack direction="row">
								<Badge sx={{ ...badgeSx }} variant="dot" color="warning" />
								<Typography variant="body1">
									{getMessage('dashboard:campaignManagement.toRelease')}
								</Typography>
							</Stack>
							<Typography
								variant="body1"
								sx={{
									fontWeight: 'bold',
								}}
							>
								{campaignContactsSummary?.toRelease}
							</Typography>
						</Stack>
					</Stack>
					{!isNilOrEmpty(selectedCampaign) && (
						<Button variant="contained" onClick={handleOpenCampaignDetail(selectedCampaign)}>
							{getMessage('dashboard:campaignManagement.campaignDetail')}
						</Button>
					)}
				</Stack>
			</ComponentLoader>
		</Paper>
	);
};

export default ContactsGraph;
